/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2024-09-03 14:05:47
 **/

import { EventEmitter } from "eventemitter3";
import { has } from "lodash-es";
import { useCallback, useRef } from "react";
import { Subscription, timer } from "rxjs";

import { useToast } from "@/frameworks/designStuff/components";
import { Properties } from "@/frameworks/libraries/properties";
import { RestAPIError } from "@/frameworks/libraries/restservice";
import { Configure } from "@/frameworks/libraries/utilities";
import { _T } from "@rfice/language";

import { useMeeting, useMyRoom, useSpace } from "@/frameworks/core/API";
import { APIErrorCode, ChatErrorCode, SPACE_SCENE_TYPE } from "@/frameworks/core/commons";
import { useUnityCommand, useUnityHandler } from "@/frameworks/core/unity";

import { useConferenceCommon } from "../hooks";

type GoOptions =
	| {
			mode: "changeSpace";
	  }
	| {
			mode: "changePortalSpace";
	  }
	| {
			mode: "friendLocation";
			data: IPC.Spaces.FriendLocation;
	  };

type GoType = SwaggerModels.UserSpaceResponseVO | SwaggerMeetingAPIModels.Conference;
type GoResponse = { origin: GoType; response: UnityResponse | ErrorResponse };
type UnityResponse = { gameChannelId: string; sceneType: number; spaceID: string };
type ErrorResponse = {
	reason: string;
};

const LOGTAG = "#useEnterScene#";
/**
 * 유니티 리소스 로딩 시간 고려
 */
const ENTER_SCENE_TIMEOUT_PER_SEC = 60 * 60;

export default function useEnterScene() {
	const unityCommand = useUnityCommand();
	const toast = useToast();
	const event = useRef(new EventEmitter());
	const spaceAPI = useSpace();
	const myRoomAPI = useMyRoom();
	const meetingAPI = useMeeting();
	const {
		actions: { setCurrentConference }
	} = useConferenceCommon();

	const handleEnterSceneSuccess = useCallback((response: GoResponse) => {
		console.debug(LOGTAG, "handleEnterSceneSuccess", response);
	}, []);

	useUnityHandler(
		"UnityEnterManager",
		{
			async onEnterSpace(payload) {
				console.debug(LOGTAG, "onEnterSpace", payload);
				event.current.emit("checkEnterScene", payload);
			}
		},
		[]
	);

	/**
	 * @description args에 sceneID로만 다루고자 하기 위해선
	 * BE API의 SpaceType별로 나뉜 API가 하나로 통일되어야함.
	 */
	const go = async (
		args: GoType,
		options: GoOptions = {
			mode: "changeSpace"
		}
	): Promise<GoResponse> => {
		let changeSpaceCommand = unityCommand.changeSpace;
		switch (options.mode) {
			case "friendLocation": {
				changeSpaceCommand = unityCommand.friendLocation;
				break;
			}
			case "changePortalSpace": {
				changeSpaceCommand = unityCommand.changePortalSpace;
				break;
			}
			default: {
				changeSpaceCommand = unityCommand.changeSpace;
				break;
			}
		}

		return new Promise<GoResponse>((resolve, reject) => {
			let timeout: Subscription | null = timer(ENTER_SCENE_TIMEOUT_PER_SEC * 1000).subscribe(() => {
				resolve({ origin: args, response: { reason: "timeout" } });
				timeout = null;
			});

			event.current.once("checkEnterScene", (payload: UnityResponse) => {
				if (timeout) {
					timeout.unsubscribe();
					timeout = null;
					handleEnterSceneSuccess({
						origin: args,
						response: payload
					});
					resolve({
						origin: args,
						response: payload
					});
				}
			});

			(async () => {
				try {
					const result = await enterProcess(args);
					console.log(LOGTAG, "after enterProcess", result);
					const data = await formatByChangeSpaceData(
						args,
						result,
						options.mode === "friendLocation" ? { ...options.data } : {}
					);
					changeSpaceCommand(data);
				} catch (error) {
					if ((error as RestAPIError).code) {
						const _error = error as RestAPIError;
						switch (_error.code) {
							case ChatErrorCode.NOT_JOINED_ROOM:
							case APIErrorCode.DATA_NOT_FOUND: {
								toast.show({
									type: "error",
									message: _T(
										"그룹&스페이스가 삭제 되었거나 더 이상 멤버가 아닙니다.\n다시 시도 해주세요"
									)
								});
								break;
							}
							case APIErrorCode.NOT_ALLOWED_SPACE_ACCESS_ALLOW_TYPE: {
								toast.show({
									type: "error",
									message: _T("스페이스 접속에 실패하였습니다.\n다시 시도 해주세요")
								});
								break;
							}
						}
					}
					reject();
				}
			})();
		});
	};
	return {
		go
	};

	async function enterProcess(args: GoType): Promise<{ useAudioZone: boolean; meetingType: "SPACE" | "CONFERENCE" }> {
		console.log(LOGTAG, "enterProcess", args);
		const defaultResponse = {
			useAudioZone: false,
			meetingType: isConference(args) ? "CONFERENCE" : "SPACE"
		} as { useAudioZone: boolean; meetingType: "SPACE" | "CONFERENCE" };

		if (!args.spaceScene) {
			return Promise.resolve(defaultResponse);
		}
		if (!isConference(args)) {
			setCurrentConference(null);
			Configure.currentConference = null;
			Configure.openingScheduleConference = null;
			switch (args.spaceScene.sceneType?.type) {
				case SPACE_SCENE_TYPE.MY_ROOM: {
					return myRoomAPI.checkBeforeParticipate
						.execute({
							myRoomId: args.spaceId,
							deviceKey: Properties.browserTabID
						})
						.then(() => defaultResponse);
				}
				case SPACE_SCENE_TYPE.LOBBY:
				case SPACE_SCENE_TYPE.MEETING_ROOM: {
					return spaceAPI.checkBeforeParticipate
						.execute({
							spaceId: args.spaceId,
							deviceKey: Properties.browserTabID
						})
						.then(() =>
							spaceAPI.detail
								.execute({
									spaceId: args.spaceId
								})
								.then((res) => ({
									useAudioZone: Boolean(res.data?.useAudioZone),
									meetingType: "SPACE"
								}))
						);
				}
				case SPACE_SCENE_TYPE.OPEN_WORLD:
					return spaceAPI.checkBeforeParticipateOpenworld
						.execute({
							spaceId: args.spaceId,
							deviceKey: Properties.browserTabID
						})
						.then(() =>
							spaceAPI.detail
								.execute({
									spaceId: args.spaceId
								})
								.then((res) => ({
									useAudioZone: Boolean(res.data?.useAudioZone),
									meetingType: "SPACE"
								}))
						);
				default: {
					return Promise.resolve(defaultResponse);
				}
			}
		} else {
			// conference
			const currentConference = {
				conferenceId: args.id!,
				accessCode: args.accessCode!,
				sceneType: args.spaceScene.sceneType?.typeNumber ?? 0,
				sceneLevel: args.spaceScene.sceneLevel!,
				path: args.spaceScene.path,
				version: args.spaceScene.version
			};
			setCurrentConference(currentConference);
			Configure.currentConference = currentConference;
			return meetingAPI.conference
				.detail({
					conferenceId: args.id!
				})
				.then(() => ({
					useAudioZone: Boolean(true),
					meetingType: "CONFERENCE"
				}));
		}
	}
}

function formatByChangeSpaceData(
	res: GoType,
	options?: {
		useAudioZone: boolean;
		meetingType?: "SPACE" | "CONFERENCE";
	},
	meta: Record<string, unknown> = {}
): IPC.Spaces.ChangeSpace {
	return {
		sceneLevel: res.spaceScene!.sceneLevel,
		sceneType: Number(res.spaceScene!.sceneType?.typeNumber),
		spaceID: isConference(res) ? res.id : res.spaceId,
		options,
		...meta
	} as IPC.Spaces.ChangeSpace;
}

function isConference(args: GoType): args is SwaggerMeetingAPIModels.Conference {
	return !has(args, "spaceId");
}
